/**
 * Money, in the currency the provider said, written the way a person reads it.
 *
 * **A provider chooses the spelling, and reading it is one rule.** Mercer
 * writes "US Dollar", Radford writes "U.S. Dollars - USD", and neither equals
 * "USD". Matching those spellings here with a pattern of their own is how the
 * two halves of this product came to disagree about one survey: the backend
 * read "U.S. Dollars - USD" as USD and filtered on it correctly, while every
 * figure on screen printed as `310,000 U.S. DOLLARS - USD`, the provider's raw
 * column value shouted back at a compensation director.
 *
 * So the spelling is resolved to a currency code first, by the same rule the
 * backend uses, and only then is a symbol looked up. One question, one answer,
 * whichever side is asking.
 *
 * **A currency we cannot resolve is named, never guessed.** The number keeps
 * the provider's own words after it rather than a symbol that might be wrong,
 * because a euro figure wearing a dollar sign is worse than one wearing
 * nothing.
 *
 * The table below mirrors `backend/src/lib/measures-stated.ts`, because the
 * frontend does not import backend code. Change both.
 */

/** The spellings a provider uses for a currency, against its code. */
const CURRENCIES: Record<string, string[]> = {
  USD: ['usd', 'us dollar', 'us dollars', 'u.s. dollar', 'u.s. dollars', 'united states dollar',
    'united states dollars', 'american dollar', 'dollar', 'dollars', '$', 'us$', 'usd$'],
  EUR: ['eur', 'euro', 'euros', '€'],
  GBP: ['gbp', 'pound', 'pounds', 'pound sterling', 'sterling', 'british pound', 'british pounds', '£'],
  INR: ['inr', 'rupee', 'rupees', 'indian rupee', 'indian rupees', '₹'],
  CAD: ['cad', 'canadian dollar', 'canadian dollars', 'c$'],
  AUD: ['aud', 'australian dollar', 'australian dollars', 'a$'],
  SGD: ['sgd', 'singapore dollar', 'singapore dollars', 's$'],
  CHF: ['chf', 'swiss franc', 'swiss francs', 'franc', 'francs'],
  JPY: ['jpy', 'yen', 'japanese yen', '¥'],
  CNY: ['cny', 'rmb', 'yuan', 'renminbi', 'chinese yuan'],
  HKD: ['hkd', 'hong kong dollar', 'hong kong dollars', 'hk$'],
  SEK: ['sek', 'krona', 'swedish krona'],
  NOK: ['nok', 'norwegian krone'],
  DKK: ['dkk', 'danish krone'],
  PLN: ['pln', 'zloty', 'polish zloty'],
  MXN: ['mxn', 'mexican peso', 'mexican pesos'],
  BRL: ['brl', 'real', 'brazilian real'],
  ZAR: ['zar', 'rand', 'south african rand'],
  AED: ['aed', 'dirham', 'uae dirham'],
  ILS: ['ils', 'shekel', 'israeli shekel'],
  KRW: ['krw', 'won', 'korean won'],
  MAD: ['mad', 'moroccan dirham'],
}

const BY_NAME = new Map<string, string>()
for (const [code, spellings] of Object.entries(CURRENCIES)) {
  BY_NAME.set(code.toLowerCase(), code)
  for (const spelling of spellings) BY_NAME.set(spelling, code)
}

/**
 * One value, read as every spelling it might be hiding.
 *
 * A provider often writes the name and the code together: "U.S. Dollars - USD"
 * is one value holding two spellings of one currency, and either half answers
 * it. The whole value is tried first, so "Canadian Dollars" is read as CAD
 * rather than falling through to the bare word "dollars" and coming out USD.
 */
function piecesOf(text: string): string[] {
  const out: string[] = []
  const add = (piece: string) => {
    const clean = piece.trim().toLowerCase().replace(/[().]+$/, '').trim()
    if (clean) out.push(clean)
  }
  add(text)
  for (const piece of text.split(/\s[-–—]\s/)) add(piece)
  for (const piece of text.split(',')) add(piece)
  for (const piece of text.split(/[\s/]+/)) add(piece)
  return out
}

/** The currency code this value names, or null where it names none. */
export function codeFor(currency: string | null | undefined): string | null {
  const text = String(currency ?? '').trim()
  if (text === '') return null
  for (const part of piecesOf(text)) {
    const found = BY_NAME.get(part)
    if (found) return found
  }
  return null
}

/**
 * The symbols in common enough use to be read without thinking.
 *
 * Deliberately short. A code written out after the number is clear; a symbol
 * most readers would have to look up is not, and one they would read as a
 * different currency is worse than none. A rand printed as "R" next to a
 * figure is a dollar sign to most people reading an American survey, so ZAR
 * keeps its code.
 */
const SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', JPY: '¥',
  CNY: '¥', CAD: 'CA$', AUD: 'A$', SGD: 'S$', CHF: 'CHF ',
}

/** The symbol for a currency, however it was spelled, or null when we know none. */
export function symbolFor(currency: string | null | undefined): string | null {
  const code = codeFor(currency)
  return code ? SYMBOLS[code] ?? null : null
}

/**
 * A figure with its currency.
 *
 * Rounded to whole units, because a market median printed to the penny is a
 * precision the survey does not have and nobody believes. A missing figure is
 * a dash rather than a zero: zero means the market pays nothing.
 */
export function money(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  const amount = Math.round(value).toLocaleString()
  const symbol = symbolFor(currency)
  if (symbol) return `${symbol}${amount}`

  /*
   * A code rather than the column, where the spelling was understood. Radford
   * writes a currency we have no symbol for as "Swedish Krona - SEK"; printing
   * "1,000 SWEDISH KRONA - SEK" is the provider's spreadsheet leaking onto a
   * screen, and "1,000 SEK" is the same fact a reader can repeat out loud.
   */
  const code = codeFor(currency)
  if (code) return `${amount} ${code}`

  const name = String(currency ?? '').trim()
  // Named rather than guessed. A spelling we cannot read at all goes after the
  // number as the provider wrote it, which is better than a wrong symbol.
  return name === '' ? amount : `${amount} ${name.toUpperCase()}`
}
